/**
 * Mijn Indeling Page (Epic C-5 — Mijn indeling & navigatie, Story C-5.1, UX-C6)
 *
 * Route `/mijn-indeling` (nav-integratie volgt in Story C-5.2, zelfde
 * incrementele opleverpatroon als `/planning/*`). Twee weekblokken ("Deze
 * week"/"Volgende week"), elk een compacte enkele-rij-variant van het
 * planningsgrid: exact dezelfde celweergave als C1/C-3.3
 * (`MijnIndelingCell.tsx`), maar volledig read-only (geen popover, geen
 * hover-ring) — dit scherm toont uitsluitend eigen data (`useMyPlanning`,
 * strikt `req.user.id`-gescoped, AD-16).
 *
 * Responsief (AC 5): één CSS-grid dat reflowt van 7 kolommen (desktop) naar
 * 1 kolom (mobiel, `grid-cols-1 sm:grid-cols-7`) — geen dubbele DOM-
 * structuur nodig, de dagkolommen stapelen vanzelf verticaal op een smal
 * scherm (elk nog steeds met de ochtend-/middagregel eronder).
 */

import { useMyPlanning } from './hooks/useMyPlanning';
import { MijnIndelingHalf } from './components/MijnIndelingCell';
import { isIndeelbaar } from './utils/myPlanningUtils';
import { getMondayOfWeek, getWeekDates, addWeeks, toDateString } from '../werkrooster/utils/dateUtils';
import { DAY_LABELS, isWerkdagNummer } from '../werkrooster/utils/weekUtils';
import { Layout } from '../../components/Layout';
import type { MyAssignmentDayEntry } from '@functieloonhuis/shared';

function summarize(days: MyAssignmentDayEntry[]): { ingedeeld: number; beschikbaar: number } {
  let ingedeeld = 0;
  let beschikbaar = 0;
  for (const day of days) {
    for (const entry of [day.morning, day.afternoon]) {
      if (entry.assignment) {
        ingedeeld += 1;
      } else if (isIndeelbaar(entry)) {
        beschikbaar += 1;
      }
    }
  }
  return { ingedeeld, beschikbaar };
}

interface MijnIndelingWeekBlockProps {
  title: string;
  testId: string;
  days: MyAssignmentDayEntry[];
}

function MijnIndelingWeekBlock({ title, testId, days }: MijnIndelingWeekBlockProps) {
  const { ingedeeld, beschikbaar } = summarize(days);

  return (
    <div data-testid={testId} className="bg-white rounded-lg border border-[#E2E8F0] p-4">
      <h2 className="text-sm font-semibold text-[#1E293B] mb-3">{title}</h2>

      <div role="grid" aria-label={title} className="grid grid-cols-1 sm:grid-cols-5 gap-1.5">
        {days.map((day) => (
          <div key={day.date} className="flex flex-col gap-1">
            <span className="text-[10px] font-medium text-gray-500 text-center">{DAY_LABELS[day.dayOfWeek]}</span>
            <MijnIndelingHalf entry={day.morning} holidayName={day.holidayName} dagLabel={DAY_LABELS[day.dayOfWeek] ?? ''} dagdeelLabel="ochtend" />
            <MijnIndelingHalf entry={day.afternoon} holidayName={day.holidayName} dagLabel={DAY_LABELS[day.dayOfWeek] ?? ''} dagdeelLabel="middag" />
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs text-gray-500">
        {ingedeeld === 0 ? 'Nog geen indelingen voor deze week.' : `${ingedeeld} dagdelen ingedeeld · ${beschikbaar} beschikbaar zonder indeling`}
      </p>
    </div>
  );
}

export function MijnIndelingPage() {
  const mondayThisWeek = getMondayOfWeek(new Date());
  const mondayNextWeek = addWeeks(mondayThisWeek, 1);
  const van = toDateString(mondayThisWeek);
  const tot = toDateString(getWeekDates(mondayNextWeek)[6]!); // zondag volgende week — samen precies 14 dagen (AD-16-clamp)

  const { data, isLoading, isError } = useMyPlanning(van, tot);
  const days = data?.days ?? [];
  // Het OPHAALvenster blijft bewust de volledige 14 dagen (AD-16-clamp en de
  // slice-indices hieronder hangen eraan); alleen de WEERGAVE laat het
  // weekend weg — besluit Friso 2026-07-29, net als elk ander weekoverzicht.
  const dezeWeek = days.slice(0, 7).filter((d) => isWerkdagNummer(d.dayOfWeek));
  const volgendeWeek = days.slice(7, 14).filter((d) => isWerkdagNummer(d.dayOfWeek));

  return (
    <Layout>
      <div data-testid="mijn-indeling-page" className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#1E293B]">Mijn indeling</h1>
          <p className="mt-1 text-sm text-gray-500">Je eigen indelingen voor deze en volgende week.</p>
        </div>

        {isLoading ? (
          <div data-testid="mijn-indeling-skeleton" className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5A7A]" />
            <span className="ml-3 text-sm text-gray-500">Indeling laden...</span>
          </div>
        ) : isError ? (
          <div data-testid="mijn-indeling-error" className="text-center py-8 text-sm text-gray-500">
            Kon je indeling niet laden. Probeer het later opnieuw.
          </div>
        ) : (
          <div className="space-y-4">
            <MijnIndelingWeekBlock title="Deze week" testId="mijn-indeling-week-deze" days={dezeWeek} />
            <MijnIndelingWeekBlock title="Volgende week" testId="mijn-indeling-week-volgende" days={volgendeWeek} />
          </div>
        )}
      </div>
    </Layout>
  );
}
